import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';
import { DicomMetadataStore } from '@ohif/core';
import { api } from 'dicomweb-client';
// file: extensions/ohif-dimse-send-button/DimseSendButton.jsx

import { Button } from '@ohif/ui'; // Componente UI de OHIF
import { apiUrl } from '../UploadDocument/apiUrl';
const noop = () => { };

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
  onRefresh,
}: withAppTypes) => {
  const [role, setRole] = useState(0);

  useEffect(() => {
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;
    const rolUser = session && session.user.role ? session.user.role : '';
    setRole(rolUser);
  }, []);
  const getTabContent = () => {
    const tabData = (tabs as string[]).find((tab: any) => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? (viewPresets as string[]).filter((preset: any) => preset.selected)[0]?.id
      : 'thumbnails';
    return tabData?.studies?.map(
      ({
        studyInstanceUid,
        date,
        description,
        patientID,
        patientName,
        ModalitiesStudy,
        numInstances,
        modalities,
        displaySets,
      }) => {
        const isExpanded = (expandedStudyInstanceUIDs as string[]).includes(studyInstanceUid);
        return (
          <React.Fragment key={studyInstanceUid}>
            <StudyItem
              date={date}
              description={description}
              numInstances={numInstances}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isExpanded}
              onClick={() => onClickStudy(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
              onRefresh={onRefresh}
            />
          </React.Fragment>
        );
      }
    );
  };

  return (
    <ScrollArea>
      <div
        className="bg-bkg-low flex flex-1 flex-col gap-[4px]"
        data-cy={'studyBrowser-panel'}
      >
        <div className="flex flex-col gap-[4px]">
          {showSettings && (
            <div className="w-100 bg-bkg-low flex h-[48px] items-center justify-center gap-[10px] px-[8px] py-[10px]">
              <>
                <StudyBrowserViewOptions
                  tabs={tabs}
                  onSelectTab={onClickTab}
                  activeTabName={activeTabName}
                />
                <StudyBrowserSort servicesManager={servicesManager} />
              </>
            </div>
          )}
          {getTabContent()}
        </div>
      </div>
    </ScrollArea>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              /**
               * Data the thumbnail should expose to a receiving drop target. Use a matching
               * `dragData.type` to identify which targets can receive this draggable item.
               * If this is not set, drag-n-drop will be disabled for this thumbnail.
               *
               * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
               */
              dragData: PropTypes.shape({
                /** Must match the "type" a dropTarget expects */
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
  StudyMenuItems: PropTypes.func,
}; // Suponiendo que tienes una URL configurada para la API de tu backend

// Función DimseSendButton
function DimseSendButton({ destination, studyInstanceUid }) {
  // Obtener metadatos del estudio usando DicomMetadataStore
  const studyMetadata = DicomMetadataStore.getStudy(studyInstanceUid);
  const studies = studyMetadata.series;
  console.log(studies); // Para ver los estudios en consola

  // Estados locales para la carga y mensajes
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Función para manejar el envío de los estudios DIMSE
  const onSend = async () => {
    try {
      // Establecer el estado de carga y limpiar el mensaje anterior
      setLoading(true);
      setMessage(null);

      // Crear el payload que será enviado al servidor
      const payload = {
        source: {
          aet: 'MY_SOURCE_AET', // AET de la fuente, configura según tu caso
          host: 'miBackendHostIP', // IP o host del backend
          port: 11112, // Puerto del backend
        },
        destination: {
          aet: destination.aet, // AET del destino (pasado como prop)
          host: destination.host, // Host del destino
          port: destination.port, // Puerto del destino
        },
        studies: studies.map(study => {
          // Verificar si la propiedad 'series' existe y es un arreglo
          if (!study.series || !Array.isArray(study.series)) {
            console.warn(`Study ${study.StudyInstanceUID} no tiene series o está mal definida.`);
            return {
              studyInstanceUID: study.StudyInstanceUID,
              archivos: [],
            };
          }

          // Retornar la estructura del estudio con las series e instancias
          return {
            studyInstanceUID: study.StudyInstanceUID,
            archivos: study.series.flatMap(series => {
              const seriesUID = series.SeriesInstanceUID;
              console.log('seriesUID', seriesUID);

              // Si no tiene 'instances' o está mal definida, devolver un arreglo vacío
              if (!series.instances || !Array.isArray(series.instances)) {
                console.warn(`Serie ${seriesUID} no tiene instancias o está mal definida.`);
                return []; // No agregar archivos para esta serie
              }

              // Filtrar y mapear las instancias que contienen PixelData
              return series.instances
                .filter(instance => instance.PixelData && instance.PixelData.BulkDataURI) // Filtrar si tiene PixelData
                .map(instance => {
                  let url = instance.PixelData.BulkDataURI;
                  // Validar la URL de BulkDataURI
                  if (!url.startsWith('http')) {
                    url = `${url}`; // Esto puede ser una validación adicional dependiendo de tu uso de URLs
                  }
                  const fileName = `DICOM_${seriesUID}_${instance.SOPInstanceUID}.dcm`; // Nombre del archivo
                  return {
                    seriesInstanceUID: seriesUID,
                    sopInstanceUID: instance.SOPInstanceUID,
                    fileName,
                    url,
                  };
                });
            }),
          };
        }),
      };

      // Enviar la solicitud al servidor
      const resp = await fetch(apiUrl + '/api/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();

      // Manejar la respuesta del servidor
      if (!resp.ok) {
        throw new Error(data.error || 'Error en envío');
      }

      // Si el envío fue exitoso, actualizar el mensaje
      setMessage('Enviado exitosamente');
    } catch (err) {
      console.error('Error al enviar estudios vía DIMSE:', err);
      setMessage('Error: ' + err.message); // Mostrar el mensaje de error
    } finally {
      setLoading(false); // Finalizar el estado de carga
    }
  };

  return (
    <div
      style={{ margin: '0.5rem' }}
      className="text-white"
    >
      <Button
        onClick={onSend}
        disabled={loading}
      >
        {loading ? 'Enviando...' : 'Enviar estudios DIMSE'}
      </Button>
      {message && <div style={{ marginTop: '0.5rem' }}>{message}</div>}
    </div>
  );
}

export { StudyBrowser };
